import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  ButtonTabs,
  Dialog,
  Icons,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Stack, Tooltip, Typography } from '@mui/material'

import { DatePicker } from 'components/DatePicker'
import IconContainer from 'components/IconContainer/IconContainer'
import NumberFormatCustom from 'components/PorcentageInput/PorcentageInput'

import { formatCentsToSendToBackEnd } from 'utils'
import { onlyNumber } from 'utils/FormatMoney'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { useCreateCoupon } from '../../api/useCreateCoupon'
import { useUpdateCoupon } from '../../api/useUpdateCoupon'
import { Coupon, CouponDiscountType } from '../../types/coupon'
import { CouponFormData, couponFormSchema } from './CouponFormModal.schema'

type CouponFormMode = 'create' | 'edit' | 'duplicate'

interface CouponFormModalProps {
  open: boolean
  handleClose: () => void
  onSuccess: (created?: Coupon) => void
  mode: CouponFormMode
  coupon?: Coupon | null
}

const LabelWithTooltip = ({
  label,
  tooltip
}: {
  label: string
  tooltip: React.ReactNode
}) => (
  <Stack direction="row" alignItems="center" gap={0.5} component="span">
    <span style={{ fontWeight: 400 }}>{label}</span>
    <Tooltip title={tooltip} arrow placement="right">
      <InfoOutlinedIcon
        sx={{ fontSize: 14, color: 'text.secondary', cursor: 'pointer' }}
      />
    </Tooltip>
  </Stack>
)

const DISCOUNT_TYPE_OPTIONS = [
  { label: 'Porcentagem', value: 'percentage' },
  { label: 'Valor fixo', value: 'fixed_value' }
]

const getTitle = (mode: CouponFormMode): string => {
  switch (mode) {
    case 'edit':
      return 'Editar cupom'
    case 'duplicate':
      return 'Duplicar cupom'
    default:
      return 'Criar cupom'
  }
}

const getIcon = (mode: CouponFormMode): Icons => {
  switch (mode) {
    case 'edit':
      return 'edit'
    default:
      return 'faTicket' as Icons
  }
}

const CouponFormModal = ({
  open,
  handleClose,
  onSuccess,
  mode,
  coupon
}: CouponFormModalProps) => {
  const { createCoupon, loading: creating } = useCreateCoupon()
  const { updateCoupon, loading: updating } = useUpdateCoupon()
  const [editConfirmOpen, setEditConfirmOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<CouponFormData | null>(
    null
  )

  const isEdit = mode === 'edit'
  const loading = creating || updating

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<CouponFormData>({
      defaultValues: {
        code: '',
        discount_type: 'percentage',
        discount_value: undefined,
        max_discount: null,
        max_usage: null,
        expires_at: null,
        single_use_per_customer: false
      },
      resolver: yupResolver(couponFormSchema)
    })

  const discountType = watch('discount_type')

  useEffect(() => {
    if (!open) return

    if (coupon && (mode === 'edit' || mode === 'duplicate')) {
      const isFixed = coupon.discount_type === 'fixed_value'
      reset({
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: (isFixed
          ? Number(coupon.discount_value).toFixed(2)
          : coupon.discount_value) as unknown as number,
        max_discount: (coupon.max_discount != null
          ? Number(coupon.max_discount).toFixed(2)
          : null) as unknown as number | null,
        max_usage: coupon.max_usage,
        expires_at: coupon.expires_at ? new Date(coupon.expires_at) : null,
        single_use_per_customer: coupon.single_use_per_customer
      })
    } else {
      reset({
        code: '',
        discount_type: 'percentage',
        discount_value: undefined,
        max_discount: null,
        max_usage: null,
        expires_at: null,
        single_use_per_customer: false
      })
    }
  }, [open, coupon, mode, reset])

  const submitForm = async (data: CouponFormData) => {
    const payload = {
      code: data.code.toUpperCase(),
      discount_type: data.discount_type as CouponDiscountType,
      discount_value: data.discount_value,
      max_discount:
        data.discount_type === 'percentage' ? data.max_discount : null,
      max_usage: data.max_usage || null,
      single_use_per_customer: data.single_use_per_customer,
      expires_at: data.expires_at ? data.expires_at.toISOString() : null
    }

    try {
      if (isEdit && coupon) {
        const updated = await updateCoupon(coupon.uuid, payload)
        toast.success(
          `Cupom ${updated?.code ?? coupon.code} atualizado com sucesso`
        )
        onSuccess()
      } else {
        const created = await createCoupon(payload)
        if (created) {
          if (mode === 'duplicate' && coupon) {
            toast.success(
              `Cupom ${created.code} criado a partir de ${coupon.code}`
            )
          } else {
            toast.success(`Cupom ${created.code} criado com sucesso`)
          }
        }
        onSuccess(created || undefined)
      }
      handleClose()
    } catch (err: any) {
      toast.error(typeof err === 'string' ? err : 'Erro ao salvar cupom')
    }
  }

  const onSubmit = (data: CouponFormData) => {
    if (isEdit) {
      setPendingFormData(data)
      setEditConfirmOpen(true)
    } else {
      submitForm(data)
    }
  }

  const handleConfirmEdit = () => {
    setEditConfirmOpen(false)
    if (pendingFormData) {
      submitForm(pendingFormData)
      setPendingFormData(null)
    }
  }

  const handleCancelEdit = () => {
    setEditConfirmOpen(false)
    setPendingFormData(null)
  }

  return (
    <>
      <Modal
        handleClose={handleClose}
        open={open}
        width="100%"
        maxWidth="520px"
        maxHeight="90vh"
        height="auto"
      >
        <ModalBody
          component="form"
          onSubmit={handleSubmit(onSubmit, toastFirstError)}
        >
          <ModalHeader onClose={handleClose}>
            <Stack direction="row" gap={2} alignItems="center">
              <IconContainer icon={getIcon(mode)} />
              <Typography variant="mdBold">{getTitle(mode)}</Typography>
            </Stack>
          </ModalHeader>

          <ModalContent>
            <Stack spacing={3}>
              <Controller
                control={control}
                name="code"
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => (
                  <Textfield
                    label={
                      <LabelWithTooltip
                        label="Código *"
                        tooltip={
                          <>
                            Código que o cliente digita para aplicar o desconto.
                            <br />• 4-20 caracteres (letras, números e hífen)
                            <br />• Ex: BLACKFRIDAY, PROMO-10, BEMVINDO
                          </>
                        }
                      />
                    }
                    placeholder="Ex: BLACKFRIDAY"
                    helperText={error?.message}
                    error={!!error}
                    value={value || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(e.target.value.toUpperCase())
                    }}
                  />
                )}
              />

              <Controller
                control={control}
                name="discount_type"
                render={({ field: { onChange, value } }) => (
                  <Stack gap={0.5}>
                    <Typography variant="smLight" color="neutral.400">
                      Tipo de desconto *
                    </Typography>
                    <ButtonTabs
                      value={value}
                      tabs={DISCOUNT_TYPE_OPTIONS}
                      onChange={(
                        _: React.SyntheticEvent,
                        newValue: string | number | boolean
                      ) => {
                        onChange(newValue)
                        setValue('discount_value', undefined as any)
                        if (newValue === 'fixed_value') {
                          setValue('max_discount', null)
                        }
                      }}
                    />
                  </Stack>
                )}
              />

              <Controller
                control={control}
                name="discount_value"
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => (
                  <Textfield
                    label="Valor do desconto *"
                    placeholder={discountType === 'percentage' ? '%' : 'R$'}
                    helperText={error?.message}
                    error={!!error}
                    value={value ?? ''}
                    mask={discountType === 'percentage' ? 'string' : 'money'}
                    {...(discountType === 'percentage' && {
                      InputProps: {
                        inputComponent: NumberFormatCustom as any
                      }
                    })}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const val = e.target.value
                      const formatted =
                        discountType === 'percentage'
                          ? val
                          : formatCentsToSendToBackEnd(onlyNumber(val))
                      onChange(formatted === '' ? undefined : formatted)
                    }}
                  />
                )}
              />

              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                onClick={() => setAdvancedOpen((v) => !v)}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  width: 'fit-content'
                }}
              >
                <Typography variant="mdBold">Opções avançadas</Typography>
                <ExpandMoreIcon
                  sx={{
                    transition: 'transform 0.2s',
                    transform: advancedOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                />
              </Stack>
              {advancedOpen && (
                <Stack spacing={3}>
                  {discountType === 'percentage' && (
                    <Controller
                      control={control}
                      name="max_discount"
                      render={({
                        field: { onChange, value },
                        fieldState: { error }
                      }) => (
                        <Textfield
                          label={
                            <LabelWithTooltip
                              label="Limite máximo de desconto"
                              tooltip="Teto em reais para o desconto calculado pela porcentagem. Se vazio, não há teto."
                            />
                          }
                          placeholder="R$"
                          helperText={error?.message}
                          error={!!error}
                          value={value ?? ''}
                          mask="money"
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const formatted = formatCentsToSendToBackEnd(
                              onlyNumber(e.target.value)
                            )
                            onChange(formatted === '' ? null : formatted)
                          }}
                        />
                      )}
                    />
                  )}

                  <Controller
                    control={control}
                    name="max_usage"
                    render={({
                      field: { onChange, value },
                      fieldState: { error }
                    }) => (
                      <Textfield
                        label={
                          <LabelWithTooltip
                            label="Quota máxima de usos"
                            tooltip="Total de usos somando todos os clientes. Ao atingir, o cupom é esgotado. Se vazio, é ilimitado."
                          />
                        }
                        placeholder=""
                        type="number"
                        helperText={error?.message}
                        error={!!error}
                        value={value ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.value
                          onChange(val === '' ? null : Number(val))
                        }}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="expires_at"
                    render={({
                      field: { onChange, value },
                      fieldState: { error }
                    }) => (
                      <DatePicker
                        value={value || null}
                        onChange={(date: any) => onChange(date)}
                        error={!!error}
                        helperText={error?.message}
                        minDate={new Date()}
                        renderInput={(props: any) => (
                          <Textfield
                            fullWidth
                            {...props}
                            label={
                              <LabelWithTooltip
                                label="Data de expiração"
                                tooltip="Data em que o cupom deixa de funcionar. Se vazio, nunca expira."
                              />
                            }
                          />
                        )}
                      />
                    )}
                  />

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Typography variant="smLight">
                        Uso único por cliente
                      </Typography>
                      <Tooltip
                        title="Cada cliente só pode usar este cupom uma vez, independente da quota total."
                        arrow
                        placement="right"
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 14,
                            color: 'text.secondary',
                            cursor: 'pointer',
                            display: 'block'
                          }}
                        />
                      </Tooltip>
                    </Stack>
                    <Controller
                      control={control}
                      name="single_use_per_customer"
                      render={({ field: { onChange, value } }) => (
                        <Switch
                          checked={value || false}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(e.target.checked)
                          }
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
          </ModalContent>

          <ModalFooter>
            <Button variantButton="neutral" onClick={handleClose} type="button">
              Cancelar
            </Button>
            <Button variantButton="primary" type="submit" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </ModalFooter>
        </ModalBody>
      </Modal>

      <Dialog
        title="Editar cupom?"
        description="As alterações só valerão para novos usos deste cupom. Contratações
            já realizadas manterão os valores originais."
        icon="info"
        setIsOpenDialog={handleCancelEdit}
        isOpenDialog={editConfirmOpen}
      >
        <Stack direction="row" gap={1} width="100%" justifyContent="end">
          <Button
            variantButton="neutral"
            onClick={handleCancelEdit}
            type="button"
            fullWidth
          >
            Cancelar
          </Button>
          <Button
            variantButton="primary"
            onClick={handleConfirmEdit}
            type="button"
            disabled={loading}
            fullWidth
          >
            Confirmar
          </Button>
        </Stack>
      </Dialog>
    </>
  )
}

export default CouponFormModal
